import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { defaultTemplate } from 'core/hoc';
import { BasicInput, BaasicButton, GlobalSignalRListener } from 'core/components';
import { Content } from 'core/layouts';
import { Editor, EditorTools } from "@progress/kendo-react-editor";
import { dropletIcon } from "@progress/kendo-svg-icons";
import BaasicModal from 'themes/components/dialog/BaasicModal';
import BaasicDropzone from 'themes/components/BaasicDropzone';
import { map } from 'lodash';

function NikoInventoryRequestEmailEditTemplate({ inventoryRequestEmailEditViewStore, t }) {
    const {
        contentLoading,
        form,
        sendEmail,
        closePage,
        body,
        isLoading,
        download,
        fetchingData,
        openUploadDocumentPopup,
        uploadDocumentModal,
        onFileDrop,
        removeAttachment,
        fileCount,
        totalFileSize,
        acceptedFiles,
        isInventoryRequestSent,
        sendManuallyModal,
        openSendManuallyPopup,
        signalRAction,
        rootStore,
        facilityOrderId,
        downloadSingleFile,
        emailSentInfo,
        isManuallyLoading
    } = inventoryRequestEmailEditViewStore;

    const {
        Bold,
        Italic,
        Underline,
        AlignLeft,
        AlignCenter,
        AlignRight,
        AlignJustify,
        Undo,
        Redo
    } = EditorTools;

    const styles = `
    body {
        font-family: Calibri, sans-serif !important; 
        color: #081420; 
    } 
    
    p { 
        margin: 0px !important; 
        font-size: 11pt; 
        line-height: 1.5;
        white-space: pre-wrap; 
        font-family: Calibri, sans-serif !important; 
    } 
   
    table { 
        table-layout: fixed !important; 
        width: 100%; 
        border: 1px solid #DDDDDD;
        border-collapse: collapse !important; 
    } 
   
    tr th {
        text-align:left; 
        border: 1px solid #ddd; 
        border-collapse: collapse !important; 
        font-size: 11pt; 
        vertical-align: top;
        padding: 3px 5px; 
    }
   
    tr td { 
        border: 1px solid #ddd; 
        border-collapse: collapse !important; 
        font-size: 11pt; 
        vertical-align: top; 
        padding: 3px 5px; 
    }
    .th-documents { 
        width: 72%; 
        color: #767171;
    }
    .th-categories { 
        width: 10%; 
        color: #767171;
    }
    .th-patients { 
        width: 18%; 
        color: #767171;
    }
    .tr-border td{ 
        border-top: 2px solid #A5A5A5 !important;
    }
    .th-categories-per-document { 
        width: 10%; 
        color: #767171;
    }
    .th-patients-per-document { 
        width: 18%; 
        color: #767171;
    }
    .table-per-document { 
        table-layout: fixed !important; 
        width: 28% !important; 
        border: 1px solid #DDDDDD; 
        border-collapse: collapse !important; }

    /* v14's ProseMirror editor leaves selection to Chrome's default
       (semi-transparent blue, blends with the back-color spans). v3's editor
       used the opaque system highlight; match it so selecting coloured text
       shows a solid highlight instead of a mixed/blended one. */
    ::selection {
        background-color: highlight;
        color: highlighttext;
    }
    `;

    const onMount = (event) => {
        const iframeDocument = event.dom.ownerDocument;
        const style = iframeDocument.createElement("style");
        style.appendChild(iframeDocument.createTextNode(styles));
        iframeDocument.head.appendChild(style);
    };

    const resize = {
        resize: 'vertical',
        overflow: "auto"
    };

   const wordPalette = [
        '#ffffff00', '#00ff00', '#00ffff','#ff00ff', 
        '#0000ff','#FF0000', '#ffff00','#000000'
    ];
 
    const CustomBackColor = (props) => (
    <EditorTools.BackColor
        {...props}
        colorPickerProps={{
            // v14: render the droplet fill glyph. v3-5's icon/iconClassName font-icon props
            // no longer resolve, leaving v14's live color-preview to show a red "no color" slash.
            svgIcon: dropletIcon,
            view: 'palette',
            paletteSettings: {
                palette: wordPalette,
                columns: 4,
                rows: 2,
                tileSize: 30,
            }
        }}
    />
    );

    return (
        <React.Fragment>
            <GlobalSignalRListener groupId={facilityOrderId} rootStore={rootStore} action={signalRAction} />
            <div className="container">
                <form onSubmit={form.onSubmit}>
                    <div className="content__main has-padding">
                        <Content loading={contentLoading || fetchingData} >
                            <div className="row">
                                <div className="col col-sml-12 col-lrg-11">
                                    <div className="card card--form card--primary card--med">
                                        <div className="form__group">
                                            <BasicInput
                                                field={form.$("to")}
                                            />
                                        </div>
                                        <div className="form__group">
                                            <BasicInput
                                                field={form.$("cc")}
                                            />
                                        </div>
                                        <div className="form__group">
                                            <BasicInput
                                                field={form.$("bcc")}
                                            />
                                        </div>
                                        <div className="form__group">
                                            <BasicInput
                                                field={form.$("replyTo")}
                                            />
                                        </div>
                                        <div className="form__group">
                                            <BasicInput
                                                field={form.$("subject")}
                                            />
                                        </div>
                                        <div className="form__group">
                                            {
                                                body ?
                                                    <Editor
                                                        resizable={true}
                                                        style={{ minHeight: 500, ...resize }}
                                                        tools={[
                                                            [Bold, Italic, Underline],
                                                            [AlignLeft, AlignCenter, AlignRight, AlignJustify],
                                                            [CustomBackColor],
                                                            [Undo, Redo],
                                                        ]}
                                                        contentStyle={{
                                                            minHeight: 442
                                                        }}
                                                        
                                                        defaultContent={body}
                                                        onMount={onMount}
                                                        onChange={(event) => {
                                                            let parser = new DOMParser();
                                                            let doc = parser.parseFromString(event.html, "text/html");
                                                            const highlightedSpans = Array.from(doc.querySelectorAll("span[style*='background-color']"));
                                                            highlightedSpans.forEach(span => {
                                                                span.style.display = "inline-block";
                                                            });
                                                            form.$("body").set(doc.body.innerHTML);
                                                        }}
                                                    />
                                                    : null
                                            }
                                        </div>
                                        <div className="form_group">
                                            <BaasicButton
                                                type='button'
                                                className="btn btn--base btn--primary u-mar--right--tny"
                                                onClick={() => openUploadDocumentPopup()}
                                                icon='u-icon u-icon--sml u-icon--attach'
                                                label={`Attach Files ${fileCount > 0 ? (" | " + fileCount) : ''}`}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Content>
                    </div>
                    {emailSentInfo && emailSentInfo.trim() !== "" && (
                        <div className="u-mar--top--med">{emailSentInfo}</div>
                    )}
                    <div className='content__footer col col-sml-12 col-lrg-11'>
                        <BaasicButton
                            className='btn btn--base btn--primary'
                            label="Send"
                            onClick={() => sendEmail()}
                            rotate icon={
                                isLoading
                                    ? "u-icon u-icon--xmed u-icon--sync u-rotate--login"
                                    : 'u-display--none'
                            }
                            disabled={isLoading || isManuallyLoading}
                        />
                        <BaasicButton
                            className='btn btn--base btn--primary'
                            label="Sent Manually"
                            onClick={() => openSendManuallyPopup()}
                            rotate icon={
                                isManuallyLoading
                                    ? "u-icon u-icon--xmed u-icon--sync u-rotate--login"
                                    : 'u-display--none'
                            }
                            disabled={isManuallyLoading || isLoading || isInventoryRequestSent}
                        />
                        <BaasicButton
                            className='btn btn--base btn--ghost'
                            label={t('EDIT_FORM_LAYOUT.CANCEL')}
                            onClick={() => closePage()}
                        />

                        <BaasicButton className="btn btn--base btn--primary u-mar--left--auto"
                            label={'Download'}
                            icon='u-icon u-icon--sml u-icon--download--white'
                            onClick={() => download()}>
                        </BaasicButton>
                    </div>
                </form>

                <BaasicModal className="modal__content modal--med" modalParams={uploadDocumentModal}>
                    <div>
                        <div className="modal__header">
                            <h2 className="modal__header__title">Attach Documents to Email</h2>
                        </div>

                        <div className="modal__body">
                            <Content loading={contentLoading}>
                                <div className="form__group">
                                    <BaasicDropzone
                                        onFilesDrop={onFileDrop}
                                        multiple={true}
                                        acceptFiles={acceptedFiles}
                                    />
                                </div>
                                <div className='message message--note'><span>Total file size: {totalFileSize} MB / {20} MB</span></div>
                                {map(form.$('files').value, form =>
                                <div className="card--secondary u-mar--top--sml u-flex--space-between" key={form.index}>
                                    <div className="k-upload-attachment">
                                        {form.fileName} ({form.fileSize} MB)
                                    </div>

                                    <BaasicButton
                                            label=""
                                            className="btn btn--base btn--icon--single"
                                            onClick={() => downloadSingleFile(form)}
                                            icon='u-icon u-icon--sml u-icon--download'
                                        />

                                    <React.Fragment>
                                        <BaasicButton
                                            className="card--secondary__close btn--shadow--sml"
                                            label=""
                                            icon="u-icon u-icon--tny u-icon--close"
                                            onClick={() => removeAttachment(form.index)}
                                        />
                                    </React.Fragment>
                                </div>
                                )}
                            </Content>

                            <div className="modal__footer is-right">
                                <BaasicButton
                                    type='button'
                                    className="btn btn--base btn--primary"
                                    label={"Finish Attaching Files"}
                                    onClick={() => uploadDocumentModal.close()}
                                />
                            </div>
                        </div>
                    </div>
                </BaasicModal>

                <BaasicModal className="modal__content modal--sml" modalParams={sendManuallyModal}>
                    <section>
                        <div className="modal__body type--center">
                            <h3 className="modal__header__title u-mar--top--med u-mar--bottom--med">Email Sent Manually</h3>
                        </div>
                            <KeyDownListener onConfirm={sendManuallyModal.onConfirm} />
                            <div className='modal__footer'>
                                    <BaasicButton
                                        type='button'
                                        className="btn btn--med btn--light"
                                        label={"Cancel"}
                                        onClick={() => sendManuallyModal.close()}
                                        disabled={isManuallyLoading}
                                    />

                                    <BaasicButton
                                        type='button'
                                        className="btn btn--med btn--primary"
                                        label={"Confirm"}
                                        disabled={isManuallyLoading}
                                        rotate icon={
                                            isManuallyLoading
                                                ? "u-icon u-icon--xmed u-icon--sync u-rotate--login"
                                                : 'u-display--none'
                                        }
                                        onClick={() => sendManuallyModal.onConfirm()}
                                    />
                            </div>                            
                    </section>
                </BaasicModal>
            </div>
        </React.Fragment>
    )
}

NikoInventoryRequestEmailEditTemplate.propTypes = {
    inventoryRequestEmailEditViewStore: PropTypes.object,
    t: PropTypes.func
};

const KeyDownListener = ({ onConfirm }) => {
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "Enter") {
                onConfirm();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, []);
    return null;
}
KeyDownListener.propTypes = {
};

export default defaultTemplate(NikoInventoryRequestEmailEditTemplate);
